// webrtc-diagnostic.service.ts
import { Injectable } from '@angular/core';
import DailyIframe from '@daily-co/daily-js';
import { IDiagnosticResult } from 'src/app/interfaces/idiagnostic-result';
import { TranslationService } from 'src/app/services/translation.service';
import { TranslocoService } from '@jsverse/transloco';
import { formatLabel } from 'src/app/utils/japanese-punctuation';

@Injectable({ providedIn: 'root' })
export class WebRTCDiagnosticService {
  private callObject: any = null;
  private videoTrack: MediaStreamTrack | null = null;
  private _ownsCallObject = false;

  constructor(
    private translationService: TranslationService,
    private translocoService: TranslocoService
  ) { }

  // Auto-selects mid-call or fresh diagnostic based on whether an active joined call exists.
  async runDiagnosticsAuto(callObject: any, isCallJoined: boolean, timeout = 30000): Promise<IDiagnosticResult> {
    if (callObject && isCallJoined) {
      return this.runDiagnosticsWithCallObject(callObject, timeout);
    }
    return this.runDiagnostics(timeout);
  }

  async runDiagnostics(timeout = 30000): Promise<IDiagnosticResult> {
    this._ownsCallObject = true;
    this.callObject = DailyIframe.createCallObject();

    // Ensure we attempt to get camera (required by TURN test in Safari).
    let startCameraError = null;
    try {
      // Add timeout to startCamera to prevent hanging
      const cameraPromise = this.callObject.startCamera({
        startVideoOff: false,
        startAudioOff: true,
      });
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Camera start timeout')), 10000)
      );

      const cam = await Promise.race([cameraPromise, timeoutPromise]) as any;

      // find a video track
      if (cam && cam.tracks) {
        const vt = Object.values(cam.tracks).find((t: any) => t.kind === 'video') as any;
        if (vt && vt.persistentTrack) {
          this.videoTrack = vt.persistentTrack;
        } else if (cam?.video && cam.video.track) {
          this.videoTrack = cam.video.track;
        }
      }

      // Try to get video track from participants if not found in cam response
      if (!this.videoTrack) {
        await new Promise(resolve => setTimeout(resolve, 100));
        const participants = this.callObject.participants();
        if (participants?.local?.tracks?.video?.persistentTrack) {
          this.videoTrack = participants.local.tracks.video.persistentTrack;
        }
      }
    } catch (err) {
      startCameraError = err;
      // Not fatal — we can still run websocket test and return a note
      console.warn('Failed to start camera for diagnostics:', err);
    }

    return this._runTests(timeout, startCameraError);
  }

  // Mid-call: accepts existing callObject, skips startCamera
  async runDiagnosticsWithCallObject(callObject: any, timeout = 30000): Promise<IDiagnosticResult> {
    this._ownsCallObject = false;
    this.callObject = callObject;
    this.videoTrack = callObject.participants()?.local?.tracks?.video?.persistentTrack ?? null;
    return this._runTests(timeout, null, true);
  }

  private async _runTests(timeout: number, startCameraError: any, isMidCall = false): Promise<IDiagnosticResult> {
    const start = Date.now();

    // 1) Websocket connectivity
    let websocketResult = null;
    try {
      websocketResult = await Promise.race([
        this.callObject.testWebsocketConnectivity({ timeout }),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Websocket test timeout')), timeout + 5000))
      ]);
    } catch (err) {
      websocketResult = { result: 'failed', error: String(err) };
      console.warn('Websocket test failed:', err);
    }

    // 2) TURN / network connectivity (requires videoTrack for Safari)
    let turnResult = null;
    if (this.videoTrack) {
      try {
        turnResult = await Promise.race([
          this.callObject.testNetworkConnectivity(this.videoTrack, { timeout }),
          new Promise((_, reject) => setTimeout(() => reject(new Error('TURN test timeout')), timeout + 5000))
        ]);
      } catch (err) {
        turnResult = { result: 'failed', error: String(err) };
        console.warn('TURN test failed:', err);
      }
    } else {
      turnResult = { result: 'skipped', reason: 'no-video-track', note: startCameraError ? String(startCameraError) : undefined };
    }

    // 3) Call quality
    // Mid-call: testCallQuality() not supported after joining — use getNetworkStats() instead
    let qualityResult = null;
    if (isMidCall) {
      try {
        const stats = await this.callObject.getNetworkStats();
        qualityResult = this.evaluateNetworkStats(stats);
      } catch (err) {
        qualityResult = { result: 'failed', error: String(err) };
        console.warn('Quality test failed:', err);
      }
    } else {
      try {
        qualityResult = await Promise.race([
          this.callObject.testCallQuality({ timeout }),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Quality test timeout')), timeout + 5000))
        ]);
      } catch (err) {
        qualityResult = { result: 'failed', error: String(err) };
        console.warn('Quality test failed:', err);
      }
    }

    const raw = { websocket: websocketResult, turn: turnResult, quality: qualityResult };

    // Create a human summary
    const summary = this.makeSummary(raw);

    // Compute hasPassed
    const hasPassed = this.computeHasPassed(websocketResult, turnResult, qualityResult);

    // cleanup
    this.cleanup();

    return {
      websocket: websocketResult,
      turn: turnResult,
      quality: qualityResult,
      timestamp: new Date().toISOString(),
      summary,
      raw,
      hasPassed,
    };
  }

  private evaluateNetworkStats(stats: any): any {
    const threshold = stats?.stats?.threshold ?? stats?.threshold;
    const latest = stats?.stats?.latest ?? stats?.latest ?? {};

    if (threshold == null) {
      return { result: 'skipped', reason: 'no-stats-available' };
    }

    const hasDegradation = threshold === 'low' || threshold === 'very-low';
    return {
      result: hasDegradation ? 'failed' : 'passed',
      data: {
        avgRoundTripTime: latest.averageNetworkRoundTripTime ?? null,
        maxRoundTripTime: latest.networkRoundTripTime ?? null,
        avgSendPacketLoss: latest.worstAudioSendPacketLoss != null
          ? +(latest.worstAudioSendPacketLoss * 100).toFixed(1)
          : null,
        avgSendBitsPerSecond: latest.audioSendBitsPerSecond
          ? Math.round(latest.audioSendBitsPerSecond)
          : null,
      },
    };
  }

  private computeHasPassed(websocket: any, turn: any, quality: any): boolean {
    const results = [websocket, turn, quality]
      .map(t => t?.result)
      .filter(r => r !== undefined && r !== 'skipped');

    return results.length > 0 && !results.includes('failed');
  }


  private makeSummary(raw: any): string {
    const parts: string[] = [];
    const na = this.translationService.translate('DIAGNOSTIC_NA', 'settings');

    if (!raw.websocket) {
      parts.push(this.translationService.translate('DIAGNOSTIC_WEBSOCKET_NOT_TESTED', 'settings'));
    } else {
      parts.push(`Websocket: ${raw.websocket.result || raw.websocket}`);
    }

    if (!raw.turn) {
      parts.push(this.translationService.translate('DIAGNOSTIC_TURN_NOT_TESTED', 'settings'));
    } else {
      parts.push(`TURN: ${raw.turn.result || raw.turn}`);
    }

    if (!raw.quality) {
      parts.push(this.translationService.translate('DIAGNOSTIC_QUALITY_NOT_TESTED', 'settings'));
    } else {
      parts.push(`Quality: ${raw.quality.result || raw.quality}`);
    }

    // optional: more details
    if (raw.quality?.data) {
      const d = raw.quality.data;
      const rttLabel = this.translationService.translate('DIAGNOSTIC_RTT_AVG_MAX', 'settings');
      const packetLossLabel = this.translationService.translate('DIAGNOSTIC_PACKET_LOSS_SEND', 'settings');
      const bitrateLabel = this.translationService.translate('DIAGNOSTIC_AVG_SEND_BITRATE', 'settings');

      const lang = this.translocoService.getActiveLang();
      parts.push(formatLabel(rttLabel, lang, `${d.avgRoundTripTime ?? na}/${d.maxRoundTripTime ?? na}s`));
      parts.push(formatLabel(packetLossLabel, lang, `${d.avgSendPacketLoss ?? na}%`));
      parts.push(formatLabel(bitrateLabel, lang, `${d.avgSendBitsPerSecond ?? na} bps`));
    }

    return parts.join(' • ');
  }

  private cleanup() {
    try {
      if (this.videoTrack && typeof (this.videoTrack as any).stop === 'function') {
        (this.videoTrack as any).stop();
      }
    } catch (e) { }

    try {
      if (this._ownsCallObject && this.callObject && typeof this.callObject.destroy === 'function') {
        this.callObject.destroy();
      }
    } catch (e) { }

    this.videoTrack = null;
    this.callObject = null;
  }
}